import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import VisuallyHidden from '@reach/visually-hidden';
import CybsButton from '@cybs-ui/components/lib/button';

import SectionHeader from './sectionHeader.component';
import styles from './base.styles';

const DropdownBase = ({
  anchorOffset,
  children,
  childKey,
  dataTest,
  idKey,
  focusRef,
  labelKey,
  defaultId,
  isMenu,
  customSort,
  dropdownWidth,
  ...rest
}) => {
  // GLOBAL VARS
  let defaultElementIdx;
  let defaultElement;

  const doesDefaultIdExist = () => defaultId && defaultId.length > 0;

  const _setDefaultElementIdx = (items, d) => {
    if (d) {
      const filteredForDefault = items.filter(item => item.id === d);

      if (filteredForDefault.length > 0) {
        const defEl = filteredForDefault[0];
        const defaultElIdx = items.indexOf(defEl);

        return defaultElIdx;
      }
    }

    return null;
  };

  if (!rest.multiselectCheckbox && doesDefaultIdExist()) {
    defaultElementIdx = _setDefaultElementIdx(rest.items, defaultId);
    defaultElement =
      defaultElementIdx > -1 ? rest.items[defaultElementIdx] : null;
  }

  const sortAlphabetical = (a, b) => {
    if (a[labelKey] > b[labelKey]) {
      return 1;
    }

    if (a[labelKey] < b[labelKey]) {
      return -1;
    }

    if (a[labelKey] === b[labelKey]) {
      return 0;
    }

    return 0;
  };

  const sortSelected = (a, b, selectedItems) =>
    selectedItems.indexOf(a) > -1 ? -1 : 1;

  const sortRecurse = (obj, func) => {
    if (!obj.children) {
      return obj;
    }
    obj.children.forEach(item => {
      if (item.children) {
        sortRecurse(item, func);
      }
    });
    obj.children = obj.children.sort(func);
    return obj;
  };

  const hilightedTextString = (item, inputValue) => {
    const input = inputValue ? inputValue.toLowerCase() : '';
    const itemStr = item[labelKey];
    const idx = itemStr && itemStr.toLowerCase().indexOf(input);

    return [
      itemStr.substring(0, idx),
      <strong key="strong-highlight">
        {itemStr.substring(idx, idx + input.length)}
      </strong>,
      itemStr.substring(idx + input.length)
    ];
  };

  const stateReducer = (state, changes) => {
    // Check for children
    const hasChildren =
      changes.selectedItem &&
      changes.selectedItem[childKey] &&
      changes.selectedItem[childKey].length !== 0;

    /**
     * Check for multiselect
     * @param {state} s
     * @param {changes} c
     */
    const multiselectItems = (s, c) => {
      const isItemInList =
        Array.isArray(s.selectedItem) &&
        s.selectedItem.indexOf(c.selectedItem) > -1;

      const onRemoveItem =
        Array.isArray(s.selectedItem) &&
        s.selectedItem.filter(i => i !== c.selectedItem);

      const onAddItem = Array.isArray(s.selectedItem)
        ? [...s.selectedItem, c.selectedItem]
        : [c.selectedItem];

      const result = isItemInList ? onRemoveItem : onAddItem;

      if (c.selectedItem) {
        if (hasChildren) {
          return c.selectedItem;
        }

        return result;
      }

      return s.selectedItem;
    };

    const highlightNextItem = (s, c) => {
      let nextIndex = c.highlightedIndex;
      if (s.isOpen) {
        if (s.highlightedIndex === rest.items.length - 1) {
          return s.highlightedIndex;
        }
        if (!rest.items[nextIndex].disabled) {
          return c.highlightedIndex;
        }
        while (
          (rest.items[nextIndex].disabled ||
            rest.items[nextIndex].isSectionHeader) &&
          nextIndex < rest.items.length - 1
        ) {
          nextIndex += 1;
        }
        return rest.items[nextIndex].disabled ||
          rest.items[nextIndex].isSectionHeader
          ? s.highlightedIndex
          : nextIndex;
      }
      return s.highlightedIndex - 1;
    };

    const highlightPreviousItem = (s, c) => {
      let prevIndex = c.highlightedIndex;
      if (s.isOpen) {
        if (s.highlightedIndex === 0) {
          return s.highlightedIndex;
        }
        if (!rest.items[prevIndex].disabled) {
          return c.highlightedIndex;
        }
        while (
          (rest.items[prevIndex].disabled ||
            rest.items[prevIndex].isSectionHeader) &&
          prevIndex > 0
        ) {
          prevIndex -= 1;
        }
        return rest.items[prevIndex].disabled ||
          rest.items[prevIndex].isSectionHeader
          ? s.highlightedIndex
          : prevIndex;
      }

      return s.highlightedIndex - 1;
    };

    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownArrowUp:
        return {
          ...changes,
          highlightedIndex: highlightPreviousItem(state, changes),
          selectedItem: state.selectedItem
        };
      case Downshift.stateChangeTypes.keyDownArrowDown:
        return {
          ...changes,
          highlightedIndex: highlightNextItem(state, changes),
          selectedItem: state.selectedItem
        };
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          selectedItem: rest.multiselectCheckbox
            ? multiselectItems(state, changes)
            : changes.selectedItem,
          highlightedIndex: state.highlightedIndex,
          inputValue: '',
          isOpen:
            hasChildren || rest.multiselectCheckbox
              ? state.isOpen
              : !state.isOpen
        };
      case Downshift.stateChangeTypes.clickButton:
      case Downshift.stateChangeTypes.blurInput:
      case Downshift.stateChangeTypes.mouseUp:
        return {
          ...changes,
          inputValue: ''
        };
      case Downshift.stateChangeTypes.keyDownEscape:
        return {
          ...changes,
          inputValue: ''
        };
      default:
        return {
          ...changes
        };
    }
  };

  const generateListChildren = (
    getItemProps,
    highlightedIndex,
    inputValue,
    selectedItem
  ) => {
    let temp = rest.items;
    if (!rest.multiselectCheckbox && doesDefaultIdExist()) {
      temp = JSON.parse(JSON.stringify(rest.items));
      temp.splice(defaultElementIdx, 1);
    }
    /* Filter temp into two arrays: One with the childKey and one without.
       sort the two arrays separately and then concat them together for further sorting */
    const childSort = temp
      .filter(item => {
        if (item[childKey] && item[childKey].length - 1) {
          return true;
        }
        return false;
      })
      .sort(sortAlphabetical);

    const childLessSort = temp
      .filter(item => {
        if (!item[childKey] || item[childKey].length <= 1) {
          return true;
        }
        return false;
      })
      .sort(sortAlphabetical);

    /* if a customSort has been passed -- apply it recursively to temp array
       then assign the result to 'items'. Otherwise assign to items the
       'default sort' aka (the spread out results from child and childless sorts) */

    const items = customSort
      ? temp.map(item => sortRecurse(item, customSort)).sort(customSort)
      : [...childSort, ...childLessSort]

          // When using multiselect, sort by selected items first
          .sort((a, b) =>
            rest.multiselectCheckbox && Array.isArray(selectedItem)
              ? sortSelected(a, b, selectedItem)
              : 0
          );
    if (
      defaultElement &&
      defaultElement.id &&
      !rest.multiselectCheckbox &&
      doesDefaultIdExist()
    ) {
      items.unshift(defaultElement);
    }

    return (
      items
        // Apply filter logic
        .filter(item =>
          rest.onFilter && inputValue ? rest.onFilter(item, inputValue) : true
        )
        .map((item, index) => (
          <React.Fragment>
            {item.isSectionHeader ? (
              <span
                key={`${item[index]}-${Math.random()}`}
                css={theme =>
                  styles({
                    isSelected: false,
                    isHighlighted: false,
                    isDisabled: false,
                    isCustomSectionHeader: true,
                    theme
                  }).listItem
                }
              >
                {SectionHeader({ text: item.value, isCustomHeader: true })}
              </span>
            ) : (
              <li
                {...getItemProps({
                  item,
                  key: `${item[labelKey]}-${
                    item[idKey]
                  }-${index}-${Math.random()}`,
                  index,
                  disabled: item.disabled
                })}
                css={theme =>
                  styles({
                    isSelected:
                      !item.disabled &&
                      item &&
                      selectedItem &&
                      item[idKey] === selectedItem[idKey] &&
                      !item[childKey] &&
                      !isMenu,
                    isHighlighted: !item.disabled && highlightedIndex === index,
                    isDisabled: item.disabled,
                    isCustomSectionHeader: false,
                    theme
                  }).listItem
                }
              >
                {/* Adds checkbox if multiselect is available & item does not have children */}
                {rest.multiselectCheckbox && !item[childKey] ? (
                  rest.multiselectCheckbox({
                    checked:
                      Array.isArray(selectedItem) &&
                      selectedItem.indexOf(item) > -1,
                    checkboxLabel: hilightedTextString(item, inputValue),
                    index,
                    item,
                    labelKey
                  })
                ) : (
                  <span>
                    {rest.onFilter && inputValue
                      ? hilightedTextString(item, inputValue)
                      : item[labelKey]}
                  </span>
                )}

                {/* A11y -- Alerts user that this item is a parent item */}
                {item[childKey] && item[childKey].length > 0 && (
                  <React.Fragment>
                    <VisuallyHidden key={`${item + index}-visually-hidden`}>
                      Parent Item. Selecting this item will show list of
                      children.
                    </VisuallyHidden>
                    <CybsButton
                      dataTest={`${dataTest}-item-arrow-next-cybsAdvancedDropdown`}
                      iconClass="arrow-next"
                      iconType={
                        highlightedIndex === index ? 'dark-tiny' : 'light-tiny'
                      }
                      resolution="tiny"
                      key={`${item + index}-next-icon`}
                      variant="icon"
                      i18n={{ ariaLabel: 'next arrow button' }}
                    />
                  </React.Fragment>
                )}
              </li>
            )}
            {Array.isArray(selectedItem) &&
              index === selectedItem.length - 1 &&
              selectedItem.indexOf(rest.items[index + 1]) === -1 &&
              rest.remainingSectionHeader}
          </React.Fragment>
        ))
    );
  };
  return (
    <Downshift
      defaultHighlightedIndex={
        rest.selectedItem && rest.items
          ? rest.items.findIndex(
              item => rest.selectedItem.value === item.value && !item.disabled
            )
          : 0
      }
      stateReducer={stateReducer}
      inputValue={rest.selectedItem && rest.selectedItem.value}
      {...rest}
    >
      {({
        getInputProps,
        getRootProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        inputValue,
        isOpen,
        selectedItem
      }) => (
        <div
          css={styles({}).wrapper}
          {...getRootProps()}
          data-test={`${dataTest}-cybsAdvancedDropdown`}
        >
          {/* Trigger Component -- will throw an error if multiple children */}
          {React.Children.only(
            children({
              dropdownWidth,
              getToggleButtonProps,
              getInputProps,
              isOpen
            })
          )}
          {isOpen ? (
            <div
              className="vds-menu"
              css={styles({ anchorOffset, dropdownWidth }).body}
            >
              {/* Optionally adds back button if nesting is supported */}
              {rest.backButton &&
                rest.backButton({ inputId: getInputProps().id })}

              {/* Optionally adds Filter row (Label / Input) */}
              {rest.filterRow &&
                rest.filterRow({
                  labelProps: getLabelProps(),
                  inputProps: getInputProps,
                  isOpen,
                  backId: `${getInputProps().id}-back-button`
                })}

              {/* Section Header */}
              {selectedItem &&
                selectedItem.children &&
                selectedItem.children.length > 0 &&
                rest.selectedHeaderEl}

              {/* Begin list */}
              <ul css={styles({}).list} {...getMenuProps({})}>
                {/* Selected items label */}
                {rest.multiselectCheckbox &&
                  Array.isArray(selectedItem) &&
                  selectedItem.length >= 1 &&
                  rest.selectedSectionHeader}

                {generateListChildren(
                  getItemProps,
                  highlightedIndex,
                  inputValue,
                  selectedItem
                )}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Downshift>
  );
};

DropdownBase.displayName = 'DropdownBase';

DropdownBase.defaultProps = {
  anchorOffset: {
    horizontal: 'center',
    vertical: 'bottom'
  },
  childKey: undefined,
  customSort: null,
  defaultId: undefined,
  focusRef: null,
  idKey: 'id',
  isMenu: false,
  itemToString: item => (item ? item.value : ''),
  dropdownWidth: '300px'
};

DropdownBase.propTypes = {
  anchorOffset: PropTypes.shape({
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'top'])
  }),
  children: PropTypes.func.isRequired,
  childKey: PropTypes.string,
  dataTest: PropTypes.string.isRequired,
  defaultId: PropTypes.string,
  focusRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  idKey: PropTypes.string,
  itemToString: PropTypes.func,
  labelKey: PropTypes.string.isRequired,
  isMenu: PropTypes.bool,
  customSort: PropTypes.func,
  dropdownWidth: PropTypes.string
};

export default DropdownBase;
